import { useEffect, useState } from 'react'
import { apiErrorMessage } from '../../../features/locations/api-errors'
import { useLocationClients, useRefreshClients } from '../../../features/locations/use-clients'
import { useLocation } from '../../../features/locations/use-location'
import { useIdOrCode } from '../../use-id-or-code'
import { Btn, Chip, Empty, Inline, SubCrumbs, td, th, utc } from './controls'
import { Bar, mono, Note, Panel, SkelPanels } from './ui'

function useElapsed(active: boolean): number {
  const [s, setS] = useState(0)
  useEffect(() => {
    if (!active) {
      setS(0)
      return
    }
    const t = setInterval(() => setS((x) => x + 1), 1000)
    return () => clearInterval(t)
  }, [active])
  return s
}

export function SignalClients() {
  const idOrCode = useIdOrCode()
  const location = useLocation(idOrCode)
  const [noIpOnly, setNoIpOnly] = useState(false)
  const clients = useLocationClients(idOrCode, { noIpOnly })
  const refresh = useRefreshClients()
  const elapsed = useElapsed(refresh.isPending)

  const crumbs = <SubCrumbs idOrCode={idOrCode} section="network" page="clients" />

  if (clients.isLoading || location.isLoading)
    return (
      <div className="flex h-full flex-col">
        <Bar crumbs={crumbs} title="Clients" />
        <div className="overflow-auto">
          <SkelPanels n={1} />
        </div>
      </div>
    )
  if (clients.error || !clients.data)
    return (
      <div className="flex h-full flex-col">
        <Bar crumbs={crumbs} title="Clients" />
        <Note error>Failed to load clients: {clients.error ? apiErrorMessage(clients.error) : 'No data.'}</Note>
      </div>
    )

  const { clients: rows, lastRefreshedAt, isStale } = clients.data
  const aptosCode = location.data?.aptosStoreCode ?? location.data?.code ?? idOrCode

  return (
    <div className="flex h-full flex-col">
      <Bar
        crumbs={crumbs}
        title="Clients"
        meta={
          <span className="flex items-center gap-2 text-[12px] text-[var(--color-muted)]" style={mono}>
            {rows.length} rows · refreshed {utc(lastRefreshedAt)}
            {isStale && <Chip tone="warn">stale</Chip>}
          </span>
        }
        right={
          <>
            <label className="flex cursor-pointer items-center gap-1.5 text-[12px] text-[var(--color-muted)]">
              <input
                type="checkbox"
                className="size-3.5 accent-[var(--color-accent)]"
                checked={noIpOnly}
                onChange={(e) => setNoIpOnly(e.target.checked)}
              />
              No IP only
            </label>
            <Btn
              variant="primary"
              disabled={refresh.isPending}
              onClick={() => refresh.mutate({ aptosCodes: [aptosCode] })}
            >
              {refresh.isPending ? `Refreshing ${elapsed}s` : 'Refresh'}
            </Btn>
          </>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <Panel
          title="Seen on network"
          aside={
            <div className="flex items-center gap-2">
              {refresh.isPending && <Inline tone="muted">collecting, usually under a minute</Inline>}
              {refresh.error && <Inline tone="bad">{apiErrorMessage(refresh.error)}</Inline>}
              {refresh.data?.status === 'complete' && <Inline tone="ok">refreshed</Inline>}
              {refresh.data?.status === 'running' && <Inline tone="muted">still collecting</Inline>}
              {refresh.data && (refresh.data.status === 'errored' || refresh.data.status === 'terminated') && (
                <Inline tone="bad">
                  {refresh.data.status}
                  {refresh.data.error ? `: ${refresh.data.error}` : ''}
                </Inline>
              )}
            </div>
          }
        >
          {rows.length === 0 ? (
            <Empty>{noIpOnly ? 'Every client has an IP address.' : 'No clients seen yet.'}</Empty>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[840px] border-collapse" aria-label="Clients">
                <thead>
                  <tr>
                    {['Hostname', 'MAC', 'IP', 'VLAN', 'Vendor', 'Seen in', 'Last seen'].map((h) => (
                      <th key={h} scope="col" className={th}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => (
                    <tr key={c.mac} className="hover:bg-[var(--color-surface-2)]">
                      <td className={td}>{c.hostname ?? '-'}</td>
                      <td className={td} style={mono}>
                        {c.mac}
                      </td>
                      <td className={td} style={mono}>
                        {c.ip ?? <Chip tone="warn">no ip</Chip>}
                      </td>
                      <td className={td} style={mono}>
                        {c.vlan ?? '-'}
                      </td>
                      <td className={`${td} text-[var(--color-muted)]`}>{c.vendor || '-'}</td>
                      <td className={`${td} text-[var(--color-muted)]`} style={mono}>
                        {c.seenIn.join(', ') || '-'}
                      </td>
                      <td className={`${td} text-[var(--color-muted)]`} style={mono}>
                        {utc(c.lastSeenAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>
    </div>
  )
}
